/**
 * Asking the agent whether it can reach a CAD host.
 *
 * The desktop's instance discovery answers "is Revit running with the plugin
 * loaded". That is not the same question as "can the agent talk to it" — the
 * agent is a separate process, it may have started before the plugin, and on a
 * locked-down machine the loopback connect can fail for it and not for the
 * desktop. `GET /health/host?port=` is the agent answering the second question
 * with its own socket, which is the only answer that predicts whether a chat
 * turn will have CAD tools.
 *
 * Two rules the shapes here exist to keep:
 *
 *   - **`offeredCount === 0` is a real answer, not a missing one.** "No host
 *     offers no CAD tools" is M1's exit criterion, so a malformed payload
 *     resolves to `null`, never to zero. Defaulting the count would make the
 *     criterion untestable from the UI.
 *   - **Unreachable is not unavailable.** The agent answering "I cannot reach
 *     that port" and the agent not answering at all are different failures with
 *     different fixes, and `AgentUnavailableError` already carries the second.
 */
import { AgentUnavailableError, type AgentClient } from "./agentClient";

export interface HostReachability {
  reachable: boolean;
  /** The agent's own connect time, in ms. */
  durationMs: number | null;
  /** Why not, in the agent's words. Absent when reachable. */
  detail: string | null;
  /** Tools the agent would offer for *this* host. `null` if it did not say. */
  offeredCount: number | null;
}

export class AgentHealthError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "AgentHealthError";
    this.status = status;
  }
}

/** A number, or `null` — never a coerced zero. See the header. */
function count(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  if (!response.ok) {
    // The agent answers 400 with `{status, detail}`. Its sentence is more useful
    // than the status code, so it is preferred when present.
    let detail = `agent returned ${response.status}`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body?.detail === "string" && body.detail) detail = body.detail;
    } catch {
      // A non-JSON body means the status code is all there is.
    }
    throw new AgentHealthError(response.status, detail);
  }
  return (await response.json()) as Record<string, unknown>;
}

/**
 * Ask the agent to reach one instance.
 *
 * `port` is required by the endpoint and is what makes this a *host* check
 * rather than a registry read — omitting it silently changes the question, so
 * it is built with `URLSearchParams` rather than concatenated.
 */
export async function checkHost(
  client: AgentClient,
  host: string,
  port: number,
): Promise<HostReachability> {
  const query = new URLSearchParams({ host, port: String(port) });
  // `/health/host` — renamed from 2.x's `/health/mcp` on 2026-08-28; the agent
  // keeps the old path as an alias for one release.
  const body = await readJson(await client.fetch(`/health/host?${query.toString()}`));
  const result = body["host"] as Record<string, unknown> | null | undefined;
  const offered = (body["offered"] ?? {}) as Record<string, unknown>;

  return {
    // Strictly `=== true`: the agent reports reachability as a boolean, and a
    // missing field means it did not ping, which is not the same as success.
    reachable: result?.["reachable"] === true,
    durationMs: count(result?.["durationMs"]),
    detail: typeof result?.["detail"] === "string" ? result["detail"] : null,
    offeredCount: count(offered["count"]),
  };
}

/** True when the failure is "there is no agent", rather than "the agent said no". */
export function isAgentUnavailable(error: unknown): boolean {
  return error instanceof AgentUnavailableError;
}
